import { Article } from "../../types/Article";
import { PalettesProps } from "../../types/palettes";
import { classifyArticles } from "../../utils/classifyArticlesHelper";
import instance from "../../services/axios";
import { getArticleMetadataByErpNumber } from "../../services/itemService";
import { openOrUpdateImageWindow } from "./sections/ELImageViewer";
import { ModuleSize } from "../../types/ModuleSize";
import { ModuleDataInspection } from "../../types/Inspection";
import { IVPoint } from "../../utils/generateIVData";

export type VisualInspectionResult = {
  classification: string;
  Comments: string;
  visualinspectiondefect: {
    defect?: string | null;
  }[];
};

export type ModuleData = {
  serialNumber?: string | null;
  productionOrderNumber?: string | null;
  pickingslipNumber?: string | null;
  elResult?: string | null;
  inspectionTime?: string | null;
  maxCurrent?: number | null;
  maxVoltage?: number | null;
  maxPower?: number | null;
  visualInspectionResult?: VisualInspectionResult | null;
  visualInspection?: ModuleDataInspection;
  elInspection?: ModuleDataInspection;
  ivCurve: IVPoint[];
};

export type ProductData = {
  pnominal: any;
  isc: any;
  uoc: any;
  tolerancepmax: any;
  toleranceisc: any;
  tolerancevcc: any;
};

export async function handleOrderSearch(
  orderNumber: string,
  setPaletteData: (palettes: PalettesProps["palettes"]) => void,
  setProductData: (productData: ProductData) => void,
  setModuleSize: (moduleSize: ModuleSize) => void
) {
  const [articleRes, moduleRes] = await Promise.all([
    instance.get(
      `/articles/GetArticleByProductionOrderNumber?production_order_number=${orderNumber}`
    ),
    instance.get(`/module/GetOrder?orderNumber=${orderNumber}`),
  ]);

  const articles: Article[] = articleRes.data;
  const modulesByProduct: Record<string, string[]> = moduleRes.data;

  const classified = classifyArticles(articles);

  const paletteKeys = ["A", "A2", "B", "C", "Q"] as const;

  type PaletteKey = (typeof paletteKeys)[number];

  const palettes = paletteKeys.reduce((acc, key) => {
    if (key === "Q") {
      const usedProductNumbers = new Set<string>(
        paletteKeys
          .filter((k): k is Exclude<PaletteKey, "Q"> => k !== "Q")
          .map((k) => classified[k]?.Product_number)
          .filter((v): v is string => Boolean(v))
      );

      const qModules = Object.entries(modulesByProduct)
        .filter(([productNumber]) => !usedProductNumbers.has(productNumber))
        .flatMap(([, modules]) => modules);

      acc[key] = {
        id: "Quality Check",
        moduleType: "Modules for verification",
        moduleOrders: qModules,
      };

      return acc;
    }

    const article = classified[key];
    if (!article) return acc;

    const moduleOrders = modulesByProduct[article.Product_number] || [];

    acc[key] = {
      id: article.Product_number,
      moduleType: article.Product_description,
      moduleOrders,
      ...article,
    };

    return acc;
  }, {} as PalettesProps["palettes"]);

  setPaletteData(palettes);

  if (palettes["A"]) {
    const productData = await getArticleMetadataByErpNumber(palettes["A"].id);
    productData && setProductData(productData);

    const size = extractModuleSize(palettes["A"].moduleType);

    if (size) {
      setModuleSize(size);
    }
  }
}

export async function handleModuleSearch(
  setModuleData: (moduleData: ModuleData) => void,
  setELImageData: (elImageData: string) => void,
  trackingNumber?: number,
  serialNumber?: string
) {
  const { data } = await instance.get(
    trackingNumber ? `/api/mesbackend/Eldata/GetElImageByTrackingNumberOrSerialNumber?imageType=backside&trackingNumber=${trackingNumber}&serialNumber=${serialNumber || ''}` : `/api/mesbackend/Eldata/GetElImageByTrackingNumberOrSerialNumber?imageType=backside&serialNumber=${serialNumber || ''}`,
    {
      responseType: "blob",
    }
  );
  const elImageData = URL.createObjectURL(data);

  const { data: moduleData }: { data: ModuleData } = serialNumber
    ? await instance.get(
      `/module/GetModuleClassificationResultsSerialNumber?serialNumber=${serialNumber}`
    )
    : await instance.get(
      `/module/GetModuleClassificationResults?trackingnumber=${trackingNumber}`
    );

  setModuleData(moduleData);
  setELImageData(elImageData);

  elImageData && moduleData && openOrUpdateImageWindow(elImageData, moduleData);
}

const extractModuleSize = (moduleName: string): ModuleSize | null => {
  const regex =
    /\b(?:6|9)?F?[A-Z]*?(S|M|L|Q)[A-Z]*[- ]?\d{2,3}(?:\.ST)?(?:\s|$)/;
  const match = moduleName.match(regex);
  return (match?.[1] as ModuleSize) ?? null;
};
